/**
 * Eine gescannte Seite fertig rechnen (Kern: kern.ts).
 *
 * Läuft in einem Web-Worker, damit Kamera und Übersicht flüssig bleiben.
 * Startet der Worker nicht (alte Browser ohne OffscreenCanvas) oder scheitert
 * er, rechnet derselbe Kern auf dem Haupt-Thread — langsamer, aber gleich.
 */
import { rendereKern, type RenderAuftrag, type RenderErgebnis } from "./kern";

export type { Drehung, RenderAuftrag, RenderErgebnis } from "./kern";

function rendereHauptthread(a: RenderAuftrag): Promise<RenderErgebnis> {
  return rendereKern(
    a,
    (w, h) => {
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      return c;
    },
    (c, q) =>
      new Promise<Blob>((res, rej) =>
        (c as HTMLCanvasElement).toBlob(
          (b) => (b ? res(b) : rej(new Error("Bild konnte nicht gespeichert werden."))),
          "image/jpeg",
          q,
        ),
      ),
  );
}

// ── Worker-Anbindung ──
type Offen = { res: (e: RenderErgebnis) => void; rej: (e: Error) => void };
let worker: Worker | null | undefined;
const offen = new Map<number, Offen>();
let zaehler = 0;

function holeWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    if (typeof OffscreenCanvas === "undefined" || typeof Worker === "undefined") {
      worker = null;
      return null;
    }
    const w = new Worker(new URL("./render.worker.ts", import.meta.url), { type: "module" });
    w.onmessage = (e: MessageEvent) => {
      const m = e.data as { id: number; ok: boolean; blob?: Blob; w?: number; h?: number; fehler?: string };
      const o = offen.get(m.id);
      if (!o) return;
      offen.delete(m.id);
      if (m.ok && m.blob) o.res({ blob: m.blob, w: m.w ?? 0, h: m.h ?? 0 });
      else o.rej(new Error(m.fehler || "Worker-Fehler"));
    };
    w.onerror = () => {
      // Worker unbrauchbar → alle offenen Aufträge auf den Haupt-Thread
      worker = null;
      offen.forEach((o) => o.rej(new Error("Worker abgestürzt")));
      offen.clear();
    };
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
}

export function rendere(a: RenderAuftrag): Promise<RenderErgebnis> {
  const w = holeWorker();
  if (!w) return rendereHauptthread(a);
  const id = ++zaehler;
  return new Promise<RenderErgebnis>((res, rej) => {
    offen.set(id, { res, rej });
    w.postMessage({ id, auftrag: a });
  }).catch(() => {
    worker = null;
    return rendereHauptthread(a);
  });
}
